import React from "react";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { HorseRaceEvent, HorseRacingPrompt, PlatformEvent, PlatformOffer } from "@freeplay/shared-code";
import { SelectionContainer } from "./SelectionContainer";
import { GameActions, GameState } from "../../controllers/GameController";
import { GlobalStyles } from "../style/Style";
import { PickEmProp } from "./PickEmProp";
import { SimpleHorseRacingProp } from "./SimpleHorseRacingProp";
import { Group } from "./Group";
import { HorseRaceWagerType } from "@freeplay/shared-code";

export interface SelectionContainerProps {
    // TODO need to update this to take in any game entry types standard sports / horse racing etc
    testID?: string;
    gameEntry?: [PlatformEvent, PlatformOffer][];
    gameEntryWithHorseEvents?: [HorseRaceEvent, HorseRacingPrompt][];
    horseProps?: HorseRacingPrompt;
    state: GameState;
    gameType: GameType;
    groupName: string;
    groupDate: Date;
    disabled?: boolean;
    dispatch: React.Dispatch<GameActions>;
    picksStartIndex: number;
}

export enum GameType {
    STANDARD = "standard",
    HORSE_RACING = "horseRacing",
}

export const GameGroup: React.FC<SelectionContainerProps> = ({
    groupName,
    groupDate,
    gameEntry,
    state,
    gameEntryWithHorseEvents,
    disabled,
    dispatch,
    picksStartIndex,
    gameType,
}) => {
    const styles = useDynamicStyles(GenerateSelectionContainerStyles);

    const displayProps = () => {
        switch (gameType) {
            case GameType.STANDARD: {
                return gameEntry?.map((entry, index) => (
                    <SelectionContainer
                        key={`selection-container-${index + picksStartIndex}`}
                        matchData={entry[0]}
                        gameData={entry[1]}
                        currentPick={state.choices[index + picksStartIndex]}
                        index={index}
                        disabled={disabled}
                        testID={"Selection-Container"}
                        isLast={index === gameEntry.length - 1}
                        incomingStyle={EStyleSheet.child(styles, "selection", index, gameEntry.length)}
                        onSelect={(choice, i) => dispatch({ type: "setSelection", choice, index: i + picksStartIndex })}
                    />
                ));
            }
            case GameType.HORSE_RACING: {
                return displayHorseRacingProps();
            }
        }
    };

    const displayHorseRacingProps = () => {
        return gameEntryWithHorseEvents?.map((entry, index) => {
            switch (entry[1].propType) {
                case HorseRaceWagerType.PICKEM: {
                    return (
                        <PickEmProp
                            key={`horse-racing-prop-${index + picksStartIndex}`}
                            numberOfPlaces={entry[1].pickEm}
                            propString="To Finish"
                            currentPick={index + picksStartIndex}
                            event={entry[0]}
                            makeHorseRacePick={dispatch}
                            reviewPicksState={disabled}
                            dispatch={dispatch}
                            incomingSelections={state.horseRaceChoices[index + picksStartIndex]}
                        />
                    );
                }
                case HorseRaceWagerType.HORSETOWIN:
                case HorseRaceWagerType.TOP4:
                case HorseRaceWagerType.WINNINGTIME:
                case HorseRaceWagerType.WINNINGDISTANCE: {
                    return (
                        <SimpleHorseRacingProp
                            key={`horse-racing-prop-${index + picksStartIndex}`}
                            propType={entry[1].propType}
                            propValue={entry[1]}
                            current={state.horseRaceChoices[index + picksStartIndex]}
                            currentPick={index + picksStartIndex}
                            makeHorseRacePick={dispatch}
                            reviewPicksState={disabled}
                        />
                    );
                }
                default:
                    return <></>;
            }
        });
    };

    return (
        <Group matchTitle={groupName} groupDate={groupDate}>
            {displayProps()}
        </Group>
    );
};

const GenerateSelectionContainerStyles = () =>
    EStyleSheet.create({
        groupContainer: {
            height: 46,
            borderTopLeftRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            borderTopRightRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            backgroundColor: () => EStyleSheet.value(GlobalStyles.PROP_HEADER),
            alignContent: "center",
            justifyContent: "center",
            textAlign: "center",
        },
        groupTitle: {
            fontSize: 16,
            lineHeight: 16,
            color: () => EStyleSheet.value(GlobalStyles.PROP_HEADER_TEXT),
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
        },
        groupDate: {
            fontSize: 12,
            lineHeight: 12,
            fontWeight: "400",
            color: () => EStyleSheet.value(GlobalStyles.PROP_HEADER_DATE),
        },
        gamesContainer: {
            padding: 10,
            borderTopWidth: 0,
            borderWidth: () => EStyleSheet.value(GlobalStyles.BORDER_THICKNESS),
            borderColor: () => EStyleSheet.value(GlobalStyles.BORDER_COLOR),
            borderBottomLeftRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            borderBottomRightRadius: () => EStyleSheet.value(GlobalStyles.BORDER_RADIUS),
            marginBottom: 10,
        },
    });
